import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  RefreshCw, ArrowUpRight, ArrowDownRight, Clock, Search, 
  Filter, X, TrendingUp, ExternalLink, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import tonService from '../services/tonService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const EnhancedTransactionMonitor = ({ address, limit = 20 }) => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(10000); // 10 seconds
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [amountFilter, setAmountFilter] = useState({ min: '', max: '' });
  const [selectedTx, setSelectedTx] = useState(null);
  const [newTxCount, setNewTxCount] = useState(0);

  const fetchTransactions = useCallback(async (silent = false) => {
    if (!address) return;

    if (!silent) setLoading(true);
    
    try {
      const data = await tonService.getTransactions(address, limit);
      
      // Check for new transactions
      if (transactions.length > 0 && data.length > 0) {
        const newTxs = data.filter(tx => 
          !transactions.find(oldTx => oldTx.hash === tx.hash)
        );
        if (newTxs.length > 0) {
          setNewTxCount(prev => prev + newTxs.length);
          toast.success(`${newTxs.length} new transaction${newTxs.length > 1 ? 's' : ''} detected`);
        }
      }
      
      setTransactions(data);
    } catch (error) {
      if (!silent) {
        toast.error('Failed to fetch transactions', {
          description: error.message,
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [address, limit, transactions]);

  useEffect(() => {
    fetchTransactions();
  }, [address]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchTransactions(true);
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchTransactions]);

  // Apply filters
  useEffect(() => {
    let filtered = [...transactions];

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(tx => tx.type === typeFilter);
    }

    // Search filter (hash or address)
    if (searchQuery) {
      filtered = filtered.filter(tx => 
        tx.hash?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tx.from_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tx.to_address?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Amount filter
    if (amountFilter.min) {
      const minAmount = parseFloat(amountFilter.min) * 1_000_000_000;
      filtered = filtered.filter(tx => parseInt(tx.amount_nano || 0) >= minAmount);
    }
    if (amountFilter.max) {
      const maxAmount = parseFloat(amountFilter.max) * 1_000_000_000;
      filtered = filtered.filter(tx => parseInt(tx.amount_nano || 0) <= maxAmount);
    }

    setFilteredTransactions(filtered);
  }, [transactions, typeFilter, searchQuery, amountFilter]);

  const formatAmount = (nanotons) => {
    if (!nanotons) return '0';
    return (parseInt(nanotons) / 1_000_000_000).toFixed(4);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(parseInt(timestamp) * 1000);
    return date.toLocaleString();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setTypeFilter('all');
    setAmountFilter({ min: '', max: '' });
    setNewTxCount(0);
  };

  const hasActiveFilters = searchQuery || typeFilter !== 'all' || amountFilter.min || amountFilter.max;

  const getTypeColor = (type) => {
    switch (type) {
      case 'TRANSFER': return 'bg-blue-100 text-blue-800';
      case 'SWAP': return 'bg-purple-100 text-purple-800';
      case 'DEPOSIT': return 'bg-green-100 text-green-800';
      case 'WITHDRAW': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'TRANSFER':
      case 'WITHDRAW':
        return <ArrowUpRight className="h-5 w-5 text-blue-600" />;
      case 'SWAP':
        return <TrendingUp className="h-5 w-5 text-purple-600" />;
      default:
        return <ArrowDownRight className="h-5 w-5 text-green-600" />;
    }
  };

  if (!address) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Enhanced Transaction Monitor</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">Connect wallet to monitor transactions</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="enhanced-transaction-monitor">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              <div>
                <CardTitle>Enhanced Transaction Monitor</CardTitle>
                <CardDescription>
                  Real-time monitoring • {filteredTransactions.length} of {transactions.length} transactions
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {newTxCount > 0 && (
                <Badge variant="default" className="animate-pulse">
                  {newTxCount} new
                </Badge>
              )}
              <Button
                size="sm"
                variant={autoRefresh ? "default" : "outline"}
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                {autoRefresh ? 'Live' : 'Paused'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => fetchTransactions()}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium">Filters</span>
              </div>
              {hasActiveFilters && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearFilters}
                  className="h-7"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search hash or address..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-9 text-sm"
                />
              </div>

              {/* Type Filter */}
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="TRANSFER">Transfer</SelectItem>
                  <SelectItem value="SWAP">Swap</SelectItem>
                  <SelectItem value="DEPOSIT">Deposit</SelectItem>
                  <SelectItem value="WITHDRAW">Withdraw</SelectItem>
                </SelectContent>
              </Select>

              {/* Min Amount */}
              <Input
                type="number"
                placeholder="Min amount (TON)"
                value={amountFilter.min}
                onChange={(e) => setAmountFilter({ ...amountFilter, min: e.target.value })}
                className="h-9 text-sm"
              />

              {/* Max Amount */}
              <Input
                type="number"
                placeholder="Max amount (TON)"
                value={amountFilter.max}
                onChange={(e) => setAmountFilter({ ...amountFilter, max: e.target.value })}
                className="h-9 text-sm"
              />
            </div>

            {/* Refresh Interval */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-600">Auto-refresh interval:</span>
              <Select 
                value={refreshInterval.toString()} 
                onValueChange={(val) => setRefreshInterval(parseInt(val))}
              >
                <SelectTrigger className="w-32 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5000">5 seconds</SelectItem>
                  <SelectItem value="10000">10 seconds</SelectItem>
                  <SelectItem value="30000">30 seconds</SelectItem>
                  <SelectItem value="60000">1 minute</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Transactions List */}
          {loading && transactions.length === 0 ? (
            <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-500">Loading transactions...</p>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">
                {hasActiveFilters ? 'No transactions match your filters' : 'No transactions yet'}
              </p>
              {hasActiveFilters && (
                <Button
                  size="sm"
                  variant="link"
                  onClick={clearFilters}
                  className="mt-2"
                >
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="space-y-2">
                {filteredTransactions.map((tx, index) => (
                  <div
                    key={tx.hash || index}
                    className="p-3 border rounded-lg hover:bg-gray-50 transition-all cursor-pointer group"
                    onClick={() => setSelectedTx(tx)}
                    data-testid={`transaction-${index}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="mt-1">{getTypeIcon(tx.type)}</div>
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <Badge className={getTypeColor(tx.type)} variant="secondary">
                              {tx.type}
                            </Badge>
                            <span className="text-sm font-bold">
                              {formatAmount(tx.amount_nano)} TON
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 font-mono break-all">
                            {tx.hash?.substring(0, 20)}...{tx.hash?.substring(tx.hash.length - 12)}
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatTimestamp(tx.timestamp)}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Transaction Details Modal */}
      <Dialog open={!!selectedTx} onOpenChange={() => setSelectedTx(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
            <DialogDescription>
              Complete information about this transaction
            </DialogDescription>
          </DialogHeader>
          {selectedTx && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Type</p>
                  <Badge className={getTypeColor(selectedTx.type)} variant="secondary">
                    {selectedTx.type}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Amount</p>
                  <p className="text-lg font-bold">{formatAmount(selectedTx.amount_nano)} TON</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Transaction Hash</p>
                <div className="flex items-center gap-2">
                  <p className="text-xs font-mono bg-gray-100 p-2 rounded break-all flex-1">
                    {selectedTx.hash}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(selectedTx.hash);
                      toast.success('Hash copied to clipboard');
                    }}
                  >
                    Copy
                  </Button>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Timestamp</p>
                <p className="text-sm">{formatTimestamp(selectedTx.timestamp)}</p>
              </div>

              {selectedTx.from_address && (
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">From</p>
                  <p className="text-xs font-mono bg-gray-100 p-2 rounded break-all">
                    {selectedTx.from_address}
                  </p>
                </div>
              )}

              {selectedTx.to_address && (
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">To</p>
                  <p className="text-xs font-mono bg-gray-100 p-2 rounded break-all">
                    {selectedTx.to_address}
                  </p>
                </div>
              )}

              {selectedTx.fee && (
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Fee</p>
                  <p className="text-sm">{formatAmount(selectedTx.fee)} TON</p>
                </div>
              )}

              {selectedTx.message && (
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Message</p>
                  <p className="text-sm bg-gray-100 p-2 rounded">{selectedTx.message}</p>
                </div>
              )}

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    window.open(`https://testnet.tonscan.org/tx/${selectedTx.hash}`, '_blank');
                  }}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View on Explorer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EnhancedTransactionMonitor;
